/**
 * String Utilities - Framework-agnostic
 * Common string manipulation functions
 */

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Capitalize first letter of each word
 */
export function titleCase(str: string): string {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ");
}

/**
 * Convert to lowercase
 */
export function lowercase(str: string): string {
  return str ? str.toLowerCase() : "";
}

/**
 * Convert to uppercase
 */
export function uppercase(str: string): string {
  return str ? str.toUpperCase() : "";
}

/**
 * Trim whitespace
 */
export function trim(str: string): string {
  return str ? str.trim() : "";
}

/**
 * Truncate string with ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (!str || str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
}

/**
 * Remove special characters
 */
export function removeSpecialChars(str: string): string {
  if (!str) return "";
  return str.replace(/[^a-zA-Z0-9\s]/g, "");
}

/**
 * Slugify string (convert to URL-friendly format)
 */
export function slugify(str: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Escape HTML special characters
 */
export function escapeHtml(str: string): string {
  if (!str) return "";
  const htmlEscapes: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return str.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
}

/**
 * Unescape HTML
 */
export function unescapeHtml(str: string): string {
  if (!str) return "";
  const htmlUnescapes: { [key: string]: string } = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
  };
  return str.replace(
    /&(?:amp|lt|gt|quot|#39);/g,
    (entity) => htmlUnescapes[entity],
  );
}

/**
 * Check if string is empty or whitespace
 */
export function isEmpty(str: string | null | undefined): boolean {
  return !str || str.trim().length === 0;
}

/**
 * Check if string contains substring (case insensitive)
 */
export function containsIgnoreCase(str: string, search: string): boolean {
  if (!str || !search) return false;
  return str.toLowerCase().includes(search.toLowerCase());
}

/**
 * Replace all occurrences
 */
export function replaceAll(
  str: string,
  search: string,
  replace: string,
): string {
  if (!str) return "";
  return str.split(search).join(replace);
}

/**
 * Pad string to specified length
 */
export function padLeft(
  str: string,
  length: number,
  padChar: string = " ",
): string {
  if (!str) str = "";
  return str.padStart(length, padChar);
}

/**
 * Pad string to specified length on right
 */
export function padRight(
  str: string,
  length: number,
  padChar: string = " ",
): string {
  if (!str) str = "";
  return str.padEnd(length, padChar);
}

/**
 * Count occurrences of substring
 */
export function countOccurrences(str: string, search: string): number {
  if (!str || !search) return 0;
  return (str.match(new RegExp(search, "g")) || []).length;
}

/**
 * Reverse string
 */
export function reverse(str: string): string {
  if (!str) return "";
  return str.split("").reverse().join("");
}

/**
 * Check if string is numeric
 */
export function isNumeric(str: string): boolean {
  if (!str) return false;
  return !isNaN(Number(str));
}

/**
 * Generate random string
 */
export function randomString(length: number = 10): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Mask string (for sensitive data)
 */
export function mask(
  str: string,
  visibleStart: number = 0,
  visibleEnd: number = 0,
  maskChar: string = "*",
): string {
  if (!str || str.length <= visibleStart + visibleEnd) return str;

  const start = str.slice(0, visibleStart);
  const end = str.slice(-visibleEnd);
  const middle = maskChar.repeat(str.length - visibleStart - visibleEnd);

  return start + middle + end;
}
